/**
 * Cross-module helpers — CRM contacts ↔ facturation clients ↔ ventes pipeline.
 */

/** Normalize CRM contact API row to display label. */
export function formatCrmContactLabel(contact) {
  if (!contact) return '';
  const name = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  const company = contact.company ? ` — ${contact.company}` : '';
  return `${name}${company}` || contact.contact_ref || String(contact.id || '');
}

/** Map CRM contact fields into facturation client form shape. */
export function crmContactToClientForm(contact, baseForm = {}) {
  if (!contact) return baseForm;
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  return {
    ...baseForm,
    company_name: contact.company || baseForm.company_name || fullName,
    contact_person: fullName || baseForm.contact_person,
    contact_fonction: contact.position || baseForm.contact_fonction || '',
    email: contact.email || baseForm.email || '',
    phone1: contact.phone || baseForm.phone1 || '',
    crm_contact_id: contact.id,
  };
}

/** Map facturation client into ventes pipeline form fields. */
export function facturationClientToPipelineForm(client, baseForm = {}) {
  if (!client) return baseForm;
  return {
    ...baseForm,
    client_name: client.company_name || client.name || baseForm.client_name,
    contact_person: client.contact_person || baseForm.contact_person,
    email: client.email || baseForm.email,
    phone: client.phone1 || client.phone || baseForm.phone,
    client_id: client.id,
  };
}

/** Map CRM contact into ventes pipeline form fields. */
export function crmContactToPipelineForm(contact, baseForm = {}) {
  if (!contact) return baseForm;
  const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim();
  return {
    ...baseForm,
    client_name: contact.company || fullName || baseForm.client_name,
    contact_person: fullName || baseForm.contact_person,
    email: contact.email || baseForm.email,
    phone: contact.phone || baseForm.phone,
    crm_contact_id: contact.id,
  };
}

/** Build CustomSelect options from CRM contacts list (facturation import key). */
export function buildCrmContactSelectOptions(contacts = []) {
  return contacts.map((c) => ({
    value: `crm:${c.id}`,
    label: formatCrmContactLabel(c),
  }));
}

/** Build CustomSelect options — contact id as value (pipeline, opportunities, interactions). */
export function buildCrmContactIdSelectOptions(contacts = []) {
  return contacts.map((c) => ({
    value: String(c.id),
    label: formatCrmContactLabel(c) || c.contact_ref || `#${c.id}`,
  }));
}

/** Build CustomSelect options from CRM sales agents (/crm/sales-agents). */
export function buildCrmSalesAgentSelectOptions(agents = []) {
  return agents.map((a) => {
    const name = `${a.first_name || ''} ${a.last_name || ''}`.trim();
    const label = name || a.code || a.agent_id || `#${a.id}`;
    return { value: name || label, label };
  });
}

/** Build CustomSelect options from facturation clients list. */
export function buildFacturationClientSelectOptions(clients = []) {
  return clients.map((c) => ({
    value: `cli:${c.id}`,
    label: c.company_name || c.name || c.client_number || String(c.id),
  }));
}

/** Facturation clients + CRM contacts — quotes, documents, support tickets. */
export function buildCrmClientPartySelectOptions(clients = [], contacts = []) {
  return [
    ...buildFacturationClientSelectOptions(clients),
    ...buildCrmContactIdSelectOptions(contacts),
  ];
}

/** Resolve party picker value (contact id or cli:id) to contact id + display label. */
export function resolveClientPartySelection(partyKey, { contacts = [], clients = [] } = {}) {
  if (!partyKey) return { contactId: null, displayName: '', partyKey: '' };
  const key = String(partyKey);
  if (key.startsWith('cli:')) {
    const client = clients.find((c) => String(c.id) === key.slice(4));
    if (!client) return { contactId: null, displayName: '', partyKey: key };
    const linked = client.crm_contact_id;
    return {
      contactId: linked != null && linked !== '' ? String(linked) : null,
      displayName: client.company_name || client.name || client.client_number || String(client.id),
      partyKey: key,
    };
  }
  const contact = contacts.find((c) => String(c.id) === key);
  return {
    contactId: key,
    displayName: contact ? formatCrmContactLabel(contact) : '',
    partyKey: key,
  };
}
